import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { EU_ALLERGENS, allergenLabel } from '@/lib/allergens';
import { useMemo, useState } from 'react';

interface AllergenMultiSelectProps {
  value: Array<string>;
  onChange: (allergens: Array<string>) => void;
}

export function AllergenMultiSelect({
  value,
  onChange,
}: AllergenMultiSelectProps) {
  const [selectedId, setSelectedId] = useState('');

  const availableItems = useMemo(() => {
    const selected = new Set(value.map((v) => v.toLowerCase()));
    return EU_ALLERGENS.filter((a) => !selected.has(a.value.toLowerCase())).map(
      (a) => ({
        id: a.id,
        value: a.value,
        label: a.label,
      }),
    );
  }, [value]);

  const handleAdd = () => {
    if (!selectedId) return;
    const allergen = EU_ALLERGENS.find((a) => a.id === selectedId);
    if (!allergen) return;
    if (value.some((v) => v.toLowerCase() === allergen.value.toLowerCase())) {
      setSelectedId('');
      return;
    }
    onChange([...value, allergen.value]);
    setSelectedId('');
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end gap-2">
        <Combobox
          id="allergens"
          label={`Allergens ${value.length}/14`}
          placeholder="Search EU allergens..."
          items={availableItems}
          value={selectedId}
          onChange={(id) => setSelectedId(id || '')}
          className="bg-white"
          emptyText="All allergens selected, or no match."
        />
        <Button
          type="button"
          variant="primary"
          onClick={handleAdd}
          className="mb-[2px]"
          disabled={!selectedId || value.length >= 14}
        >
          Add
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md bg-gray-100 p-3">
          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
            Declared allergens
          </span>
          <div className="flex flex-col gap-2">
            {value.map((allergen, index) => (
              <div
                key={`${allergen}-${index}`}
                className="flex items-center justify-between pb-2"
              >
                <span className="text-sm font-medium text-slate-700">
                  {allergenLabel(allergen)}
                </span>
                <RelationActionButton
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-delete"
                >
                  Remove
                </RelationActionButton>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
